import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Fab,
  Tooltip,
  DialogContentText,
  Container,
  Avatar,
  CardMedia,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  SubdirectoryArrowRight as SubdirectoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import BackBtn from "../widgets/BackBtn";

const API_BASE = `${API_ENDPOINT}user`;

const CategoriesList = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Dialog states
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newSubcategory, setNewSubcategory] = useState("");

  // Image handling states
  const [categoryImage, setCategoryImage] = useState(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API_BASE}/product-categories`,
        getHeader()
      );
      setCategories(res.data || []);

      const subRes = await axios.get(
        `${API_BASE}/get-product-subcategories`,
        getHeader()
      );
      setSubcategories(subRes.data || []);
    } catch (err) {
      showError("Failed to load categories");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError("Image size should be less than 5MB");
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        showError("Only JPG, PNG, and WebP images are allowed");
        return;
      }

      setCategoryImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCategoryImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setCategoryImage(null);
    setCategoryImagePreview("");
  };

  const resetCategoryDialog = () => {
    setNewCategory("");
    setCategoryImage(null);
    setCategoryImagePreview("");
    setEditMode(false);
    setEditingCategory(null);
    setOpenCategoryDialog(false);
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("category", newCategory);
      if (categoryImage) {
        formData.append("image", categoryImage);
      }

      await axios.post(`${API_BASE}/add-product-category`, formData, {
        ...getHeader(),
        headers: {
          ...getHeader().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      showSuccess("Category added successfully!");
      resetCategoryDialog();
      fetchCategories();
    } catch (err) {
      showError("Failed to add category");
      console.error(err);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEditCategory = async () => {
    if (!newCategory.trim() || !editingCategory) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("category", newCategory);
      if (categoryImage) {
        formData.append("image", categoryImage);
      }

      await axios.put(
        `${API_BASE}/update-product-category/${editingCategory._id}`,
        formData,
        {
          ...getHeader(),
          headers: {
            ...getHeader().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      showSuccess("Category updated successfully!");
      resetCategoryDialog();
      fetchCategories();
    } catch (err) {
      showError("Failed to update category");
      console.error(err);
    } finally {
      setUploadingImage(false);
    }
  };

  const openEditDialog = (category) => {
    setEditMode(true);
    setEditingCategory(category);
    setNewCategory(category.category);
    if (category.image) {
      setCategoryImagePreview(category.image);
    }
    setOpenCategoryDialog(true);
  };

  const handleAddSubcategory = async () => {
    if (!newSubcategory.trim() || !selectedCategoryId) return;
    try {
      await axios.post(
        `${API_BASE}/add-subcategory-product`,
        {
          categoryId: selectedCategoryId,
          subCategory: newSubcategory,
        },
        getHeader()
      );
      showSuccess("Subcategory added successfully!");
      setNewSubcategory("");
      setOpenSubcategoryDialog(false);
      fetchCategories();
    } catch (err) {
      showError("Failed to add subcategory");
      console.error(err);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(
        `${API_BASE}/delete-product-category/${deleteTarget.id}`,
        getHeader()
      );
      showSuccess("Category and its subcategories deleted successfully!");
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      fetchCategories();
    } catch (err) {
      showError("Failed to delete category");
      console.error(err);
    }
  };

  const handleDeleteSubcategory = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(
        `${API_BASE}/delete-subcategory/${deleteTarget.id}`,
        getHeader()
      );
      showSuccess("Subcategory deleted successfully!");
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      fetchCategories();
    } catch (err) {
      showError("Failed to delete subcategory");
      console.error(err);
    }
  };

  const confirmDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setOpenDeleteDialog(true);
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 4000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 5000);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <BackBtn />
      <Typography
        variant="h4"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
      >
        <CategoryIcon fontSize="large" /> Product Categories
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCategoryDialog(true)}
          size="large"
        >
          Add Category
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : categories.length === 0 ? (
        <Alert severity="info">No categories yet. Create your first one!</Alert>
      ) : (
        <List>
          {categories.map((cat) => {
            const subs = subcategories.filter(
              (sub) => sub.categoryId === cat._id
            );
            return (
              <Accordion key={cat._id} elevation={2} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      gap: 2,
                    }}
                  >
                    {cat.image ? (
                      <Avatar
                        src={cat.image}
                        alt={cat.category}
                        variant="rounded"
                        sx={{ width: 56, height: 56 }}
                      />
                    ) : (
                      <Avatar
                        variant="rounded"
                        sx={{ width: 56, height: 56, bgcolor: "primary.light" }}
                      >
                        <CategoryIcon />
                      </Avatar>
                    )}
                    <ListItemText
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          {cat.category}
                        </Typography>
                      }
                      secondary={
                        <Chip
                          label={`${subs.length} subcategories`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignSelf: "center",
                      gap: 0.5,
                      mr: 1,
                    }}
                  >
                    <Tooltip title="Edit Category">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(cat);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add Subcategory">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategoryId(cat._id);
                          setOpenSubcategoryDialog(true);
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Category">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete("category", cat._id, cat.category);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {subs.length === 0 ? (
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{ pl: 2 }}
                    >
                      No subcategories yet. Click the + icon to add one.
                    </Typography>
                  ) : (
                    <List dense>
                      {subs.map((sub) => (
                        <ListItem
                          key={sub._id}
                          sx={{
                            border: "1px solid",
                            borderColor: "grey.200",
                            borderRadius: 1,
                            mb: 0.5,
                          }}
                        >
                          <SubdirectoryIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "grey.500" }}
                          />
                          <ListItemText primary={sub.subCategory} />
                          <ListItemSecondaryAction>
                            <Tooltip title="Delete Subcategory">
                              <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                onClick={() =>
                                  confirmDelete(
                                    "subcategory",
                                    sub._id,
                                    sub.subCategory
                                  )
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </List>
      )}

      {/* Add/Edit Category Dialog */}
      <Dialog
        open={openCategoryDialog}
        onClose={resetCategoryDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit Category" : "Add New Category"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" &&
              !uploadingImage &&
              (editMode ? handleEditCategory() : handleAddCategory())
            }
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Category Image (Optional)
            </Typography>

            {categoryImagePreview ? (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <CardMedia
                  component="img"
                  image={categoryImagePreview}
                  alt="Category preview"
                  sx={{
                    width: 200,
                    height: 200,
                    objectFit: "cover",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={clearImage}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "error.light", color: "white" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<ImageIcon />}
                sx={{ mt: 1 }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageSelect}
                />
              </Button>
            )}

            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 1, color: "text.secondary" }}
            >
              Recommended: Square image, max 5MB (JPG, PNG, WebP)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetCategoryDialog} disabled={uploadingImage}>
            Cancel
          </Button>
          <Button
            onClick={editMode ? handleEditCategory : handleAddCategory}
            variant="contained"
            disabled={!newCategory.trim() || uploadingImage}
            startIcon={uploadingImage && <CircularProgress size={16} />}
          >
            {uploadingImage
              ? "Uploading..."
              : editMode
              ? "Update Category"
              : "Add Category"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subcategory Dialog */}
      <Dialog
        open={openSubcategoryDialog}
        onClose={() => setOpenSubcategoryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Subcategory</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subcategory Name"
            fullWidth
            variant="outlined"
            value={newSubcategory}
            onChange={(e) => setNewSubcategory(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddSubcategory()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubcategoryDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSubcategory}
            variant="contained"
            disabled={!newSubcategory.trim()}
          >
            Add Subcategory
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.name}</strong>?
            {deleteTarget?.type === "category" && (
              <>
                <br />
                <br />
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This will also delete all subcategories under this category.
                </Alert>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={
              deleteTarget?.type === "category"
                ? handleDeleteCategory
                : handleDeleteSubcategory
            }
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add category"
        sx={{ position: "fixed", bottom: 24, right: 24 }}
        onClick={() => setOpenCategoryDialog(true)}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default CategoriesList;
